import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SUPPORT_EMAIL } from "../../constants/emails";
import {
  ContractTypeValuesFor,
  PrePurchaseStageKey,
  ProductTypes,
  StageValues,
} from "../../constants/purchaseFlow";
import { usePrePurchaseURLParams } from "../../hooks/usePrePurchaseURLParams";
import { captureError } from "../../utils/sentry";
import { Button } from "../coreUI/button";
import { ExternalLinkText } from "../coreUI/ExternalLink";
import { SamphireNeuroscienceLogo } from "../ui/logos/samphireNeuroscienceLogo";
import { ContractTypeOption } from "./ContractTypeOption";
import { ProductOption } from "./ProductOption";
import { ProgressBar } from "./ProgressBar";

interface PrePurchaseUserFlowProps {
  stageURLParam: PrePurchaseStageKey;
  productURLParam: ProductTypes;
  contractTypeURLParam?: ContractTypeValuesFor<ProductTypes>;
}

export function PrePurchaseUserFlow({
  stageURLParam,
  productURLParam,
  contractTypeURLParam,
}: PrePurchaseUserFlowProps) {
  // Debugging for email verification flow
  console.log("🔴 PrePurchaseUserFlow mounted!");
  console.log("🔴 URL params:", {
    stageURLParam,
    productURLParam,
    contractTypeURLParam,
  });
  console.log("🔴 Current URL:", window.location.href);
  const [nextButtonEnabled, setNextButtonEnabled] = useState(false);
  const { setURLParam } = usePrePurchaseURLParams();

  // Set default product and contract type based on stage
  useEffect(() => {
    // // For stage 1, set default product to nettle if not already set
    // if (stageURLParam === 1 && !productURLParam) {
    //   setURLParam("product", "nettle");
    // }

    // reset the contract type when going back from stage 2 to stage 1
    if (stageURLParam === 1 && contractTypeURLParam) {
      setURLParam("contractType", undefined);
    }

    // For stage 2, set default contract type based on selected product
    if (stageURLParam === 2 && !contractTypeURLParam) {
      if (productURLParam === "nettle") {
        setURLParam("contractType", "onceOff");
      } else if (productURLParam === "membershipOnly") {
        setURLParam("contractType", "annual");
      }
    }
  }, [stageURLParam, productURLParam, contractTypeURLParam, setURLParam]);

  const navigate = useNavigate();

  const getCheckoutRoute = () => {
    console.log("🔍 PrePurchaseUserFlow: getCheckoutRoute called", {
      productURLParam,
      contractTypeURLParam,
    });

    // Based on product and contract type selections, determine the checkout route
    if (productURLParam === "nettle") {
      if (contractTypeURLParam === "monthly") {
        // Rent to own Nettle -> RevenueCat checkout for monthly Nettle membership
        const route = `/checkout/subscription/${encodeURIComponent("$sn_nettle_12_mo")}`;
        console.log(
          "🔍 PrePurchaseUserFlow: Routing to RevenueCat subscription:",
          route,
        );
        return route;
      } else if (contractTypeURLParam === "onceOff") {
        // Own Nettle for Life -> Shopify checkout for Nettle
        const route = `/checkout/product/nettle`;
        console.log(
          "🔍 PrePurchaseUserFlow: Routing to Shopify product:",
          route,
        );
        return route;
      }
    } else if (productURLParam === "membershipOnly") {
      if (contractTypeURLParam === "annual") {
        // RevenueCat checkout for annual app membership
        const route = `/checkout/subscription/${encodeURIComponent("$rc_annual")}`;
        console.log(
          "🔍 PrePurchaseUserFlow: Routing to RevenueCat annual:",
          route,
        );
        return route;
      } else if (contractTypeURLParam === "monthly") {
        // RevenueCat checkout for monthly app membership
        const route = `/checkout/subscription/${encodeURIComponent("$rc_monthly")}`;
        console.log(
          "🔍 PrePurchaseUserFlow: Routing to RevenueCat monthly:",
          route,
        );
        return route;
      }
    }

    // Fallback - shouldn't reach here with valid selections
    console.log("🔍 PrePurchaseUserFlow: Fallback route - invalid selection");
    captureError(
      new Error("Invalid selection in PrePurchaseUserFlow: getCheckoutRoute"),
      {
        productURLParam,
        contractTypeURLParam,
      },
    );

    return "/products";
  };

  function handleBackButtonPress() {
    const previousStage = stageURLParam - 1;
    if (previousStage >= 1) {
      setURLParam("stage", previousStage.toString() as StageValues);
    }
    if (previousStage == 0) {
      window.location.href = "https://www.samphireneuro.com";
    }
    return;
  }

  useEffect(() => {
    setNextButtonEnabled(
      (stageURLParam === 1 && productURLParam === "nettle") ||
        (stageURLParam === 1 && productURLParam === "membershipOnly") ||
        (stageURLParam === 2 &&
          productURLParam === "nettle" &&
          contractTypeURLParam === "monthly") ||
        (stageURLParam === 2 &&
          productURLParam === "nettle" &&
          contractTypeURLParam === "onceOff") ||
        (stageURLParam === 2 &&
          productURLParam === "membershipOnly" &&
          contractTypeURLParam === "annual") ||
        (stageURLParam === 2 &&
          productURLParam === "membershipOnly" &&
          contractTypeURLParam === "monthly"),
    );
  }, [stageURLParam, productURLParam, contractTypeURLParam]);

  function handleNextButtonPress() {
    if (
      (stageURLParam === 1 && productURLParam === "nettle") ||
      (stageURLParam === 1 && productURLParam === "membershipOnly")
    ) {
      // Move from stage 1 to stage 2
      setURLParam("stage", (stageURLParam + 1).toString() as StageValues);
    } else if (
      (stageURLParam === 2 &&
        productURLParam === "nettle" &&
        contractTypeURLParam === "monthly") ||
      (stageURLParam === 2 &&
        productURLParam === "nettle" &&
        contractTypeURLParam === "onceOff") ||
      (stageURLParam === 2 &&
        productURLParam === "membershipOnly" &&
        contractTypeURLParam === "annual") ||
      (stageURLParam === 2 &&
        productURLParam === "membershipOnly" &&
        contractTypeURLParam === "monthly")
    ) {
      // Navigate directly to checkout after stage 2
      const checkoutRoute = getCheckoutRoute();
      navigate(checkoutRoute);
    }
  }

  useEffect(() => {
    if (stageURLParam === 2 && !productURLParam) {
      setURLParam("stage", "1");
    }
  }, [stageURLParam, productURLParam]);

  return (
    <div className="relative w-full h-full bg-canvas-base flex flex-col items-center mt-8 px-2 lg:px-0 lg:pr-4">
      <div className="w-full mt-16 sm:mt-8">
        <ProgressBar currentStage={stageURLParam} numberOfStages={3} />
      </div>
      {stageURLParam === 1 && (
        <div className="flex flex-col gap-4 w-full">
          <ProductOption variant="nettle" productUrlParam={productURLParam} />
          <ProductOption
            variant="membershipOnly"
            productUrlParam={productURLParam}
          />
        </div>
      )}
      {stageURLParam === 2 && (
        <div className="flex flex-col gap-4 w-full">
          {productURLParam === "nettle" && (
            <>
              <ContractTypeOption
                product={productURLParam}
                variant="onceOff"
                contractTypeUrlParam={contractTypeURLParam}
              />
              <ContractTypeOption
                product={productURLParam}
                variant="monthly"
                contractTypeUrlParam={contractTypeURLParam}
              />
            </>
          )}
          {productURLParam === "membershipOnly" && (
            <>
              <ContractTypeOption
                product={productURLParam}
                variant="annual"
                contractTypeUrlParam={contractTypeURLParam}
              />
              <ContractTypeOption
                product={productURLParam}
                variant="monthly"
                contractTypeUrlParam={contractTypeURLParam}
              />
            </>
          )}
        </div>
      )}
      {(stageURLParam === 1 || stageURLParam === 2) && (
        <div className="flex flex-col mt-8 gap-4 w-full ">
          <Button
            disabled={!nextButtonEnabled}
            variant="primary"
            onClick={handleNextButtonPress}
            fullWidth
            size="default"
          >
            Next
          </Button>
        </div>
      )}
      {stageURLParam === 2 && productURLParam === "nettle" && (
        <div className="mt-8 w-full">
          <OtherGeographyCTA />
        </div>
      )}
      <div className="absolute w-full flex flex-row justify-between top-0 left-0 right-0 md:-top-3 pl-4 lg:pl-0 pr-4">
        <div>
          <Button
            size="small"
            variant="secondary"
            onClick={handleBackButtonPress}
          >
            Back
          </Button>
        </div>
        <SamphireNeuroscienceLogo width={100} className=" text-black mb-4" />
      </div>
    </div>
  );
}

function OtherGeographyCTA() {
  // Email constants for support contact
  const SUPPORT_EMAIL_TEXT = {
    SUBJECT_LINE: "Nettle Delivery Options Inquiry",
    EMAIL_BODY:
      "Hi Samphire team,\n\n{Let us know your country, and we'll get back to you with options as soon as possible}",
  } as const;

  const mailtoLink = `mailto:${SUPPORT_EMAIL.TO_ADDRESS}?subject=${encodeURIComponent(SUPPORT_EMAIL_TEXT.SUBJECT_LINE)}&body=${encodeURIComponent(SUPPORT_EMAIL_TEXT.EMAIL_BODY)}`;

  return (
    <div className="w-full rounded-2xl bg-blue-100 hover:bg-blue-200 text-brand-primary-darker text-left px-4 py-4 hover:text-blue-800">
      <div className="font-bold">Living somewhere else?</div>
      <ExternalLinkText
        href={mailtoLink}
        text="Contact support for options"
        className="text-brand-primary-darker shrink-0 hover:text-blue-800"
      />
    </div>
  );
}
